import { useState, useRef, useEffect } from 'react';
import NavBar from './components/NavBar.js';
import Landing from './components/Landing.js';
import Events from './components/Events.js';
import Promoters from './components/Promoters.js';
import Partners from './components/Partners.js';
import ApiDocs from './components/ApiDocs.js';
import { DataProvider, useData } from './context/DataContext.js';
import LoginPanel from './components/LoginPanel.js';
import { userSession } from './utils/userSession.js';
import UserDashboard from './components/UserDashboard.js';
import CookieConsent from './components/CookieConsent.js';
import VersionChecker from './components/VersionChecker.js';
import PrivacyPolicyPage from './components/PrivacyPolicyPage.js';
import TermsOfServicePage from './components/TermsOfServicePage.js';
import ReactGA4 from 'react-ga4';
import { getCookieConsent } from './utils/cookies.js';
import { logFacebookPageView } from './utils/facebookSdk.js';

const PAGE_PATHS = {
  landing: '/',
  events: '/events',
  promoters: '/promoters',
  partners: '/partners',
  api: '/api',
  privacy: '/privacy',
  termsofservice: '/termsofservice'
};

function pageFromPath(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  const match = Object.entries(PAGE_PATHS).find(([, href]) => href === path);
  return match ? match[0] : null;
}

function pathForPage(page) {
  return PAGE_PATHS[page] || '/';
}

// Import AWS Amplify and the configuration
//import { Amplify } from 'aws-amplify';
//import awsconfig from './aws-exports.js';  // Ensure this file exists and contains the correct configuration

// Configure Amplify with the configuration file
//Amplify.configure(awsconfig);

function AppContent() {
  const [currentPage, setCurrentPage] = useState(
    () => pageFromPath(window.location.pathname) || 'landing'
  );
  const [showLogin, setShowLogin] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const dashboardRef = useRef();
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [, setUser] = useState(userSession.getUser());
  const { countries, setSelectedCountry } = useData();

  useEffect(() => {
    // Subscribe to session changes
    const unsubscribe = userSession.subscribe((newUser) => {
      setUser(newUser);
    });
    
    return () => unsubscribe();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    userSession.setUser(userData);
    setShowLogin(false);
    setLoginMessage('');
    setIsDashboardOpen(true);
  };

  const handleLogout = () => {
    userSession.clearUser();
  };

  const handleNavigate = (page) => {
    const nextPath = pathForPage(page);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
    setCurrentPage(page);
  };

  useEffect(() => {
    const onPopState = () => {
      setCurrentPage(pageFromPath(window.location.pathname) || 'landing');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleOpenLogin = (message) => {
    const updates = () => {
      if (userSession.isLoggedIn()) {
        setShowLogin(false);
        setIsDashboardOpen(true);
      } else {
        setIsDashboardOpen(false);
        setLoginMessage(message);
        setShowLogin(true);
      }
    };
    requestAnimationFrame(updates);
  };

  const handleOpenDashboard = () => {
    setIsDashboardOpen(true);
  };

  useEffect(() => {
    // Handle return from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const userData = urlParams.get('user_data');
    const showDashboard = urlParams.get('show_dashboard');
    const refreshData = urlParams.get('refresh_data');
    
    if (sessionId && userData) {
      try {
        // Restore the user session
        const parsedUserData = JSON.parse(userData);
        userSession.setUser(parsedUserData);
        
        // Show dashboard if requested
        if (showDashboard === 'true') {
          setIsDashboardOpen(true);
        }

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Refresh data context if needed
        if (refreshData === 'true' && dashboardRef.current?.handleEventsChange) {
          dashboardRef.current.handleEventsChange();
        }
      } catch (err) {
        console.error('Error restoring user session:', err);
      }
      try {
        const trackingId = process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID || 'G-7CZZ52KDLS';
        ReactGA4.initialize(trackingId);
        
        // Send initial pageview
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname
        });
      } catch (error) {
        console.error('GA4 initialization error:', error);
      }
    }
  }, []);

  // Track page views when currentPage changes
  useEffect(() => {
    const titles = {
      landing: 'DriftCal',
      events: 'Events | DriftCal',
      promoters: 'Promoters | DriftCal',
      partners: 'Partners | DriftCal',
      api: 'API | DriftCal',
      privacy: 'Privacy policy | DriftCal',
      termsofservice: 'Terms of service | DriftCal'
    };
    document.title = titles[currentPage] || 'DriftCal';
    handleRouteChange({ pathname: pathForPage(currentPage) });
  }, [currentPage]);

  // Scroll to top when switching between pages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handleRouteChange = (location) => {
    if (getCookieConsent('analytics')) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname
      });
      logFacebookPageView();
    }
  };

  // Check for existing session when app loads
  useEffect(() => {
    if (userSession.isLoggedIn()) {
      // User has a valid session
    }
  }, []);

  useEffect(() => {
    const handleUrlParams = () => {
      // Get URL parameters without react-router
      const urlParams = new URLSearchParams(window.location.search);
      const country = urlParams.get('country');
      const showEvents = urlParams.get('events');
      const promoterName = urlParams.get('promoter');

      const pathPage = pageFromPath(window.location.pathname);
      if (pathPage === 'privacy' || pathPage === 'termsofservice') {
        return;
      }

      if (showEvents === 'true' || country || promoterName) {
        setCurrentPage('events');
        
        if (country && countries) {
          const countryData = countries.find(c => c.CountryCode === country.toUpperCase());
          if (countryData) {
            setSelectedCountry(countryData);
          }
        }
      }
    };

    handleUrlParams();
  }, [countries, setSelectedCountry, setCurrentPage]);

  return (
    <div className="min-h-screen bg-slate-800">
      <div className="relative isolate min-h-full pt-16">
        {/* Fixed background image - add pointer-events-none */}
        <img
          src="/driftcal_1000_black.png"
          alt=""
          className="fixed inset-0 -z-20 h-full w-full object-cover object-center opacity-10 scale-150 pointer-events-none"
        />
        
        {/* Background gradients - add pointer-events-none */}
        <div className="fixed inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 pointer-events-none" aria-hidden="true">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[54.1875rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[108.28125rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
        
        <NavBar 
          onNavigate={handleNavigate} 
          currentPage={currentPage} 
          onOpenLogin={handleOpenLogin}
          onOpenDashboard={handleOpenDashboard}
        />

        {currentPage === 'landing' && <Landing onNavigate={handleNavigate} />}
        {currentPage === 'events' && (
          <Events 
            onOpenLogin={(message) => handleOpenLogin(message)}
            onEventAdded={() => {
              if (dashboardRef.current?.handleEventsChange) {
                dashboardRef.current.handleEventsChange();
              }
            }}
            initialPromoter={new URLSearchParams(window.location.search).get('promoter')}
          />
        )}
        {currentPage === 'promoters' && <Promoters />}
        {currentPage === 'partners' && <Partners />}
        {currentPage === 'api' && (
          <ApiDocs
            onOpenLogin={handleOpenLogin}
            onOpenDashboard={handleOpenDashboard}
          />
        )}
        {currentPage === 'privacy' && <PrivacyPolicyPage />}
        {currentPage === 'termsofservice' && <TermsOfServicePage />}

        <UserDashboard 
          ref={dashboardRef}
          isOpen={isDashboardOpen} 
          onClose={() => {
            setIsDashboardOpen(false);
          }} 
          user={userSession.getUser()}
        />

        <LoginPanel 
          isOpen={showLogin}
          onClose={() => {
            setShowLogin(false);
            setLoginMessage('');
            setIsDashboardOpen(false);
          }}
          message={loginMessage}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onOpenDashboard={() => setIsDashboardOpen(true)}
        />

        {/* Bottom gradient - add pointer-events-none */}
        <div className="fixed inset-x-0 bottom-0 -z-10 transform-gpu overflow-hidden blur-3xl pointer-events-none" aria-hidden="true">
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[54.1875rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[108.28125rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <DataProvider>
      <AppContent />
      <CookieConsent />
      <VersionChecker />
    </DataProvider>
  );
}
